import React from 'react';
import PropTypes from 'prop-types';
import {useAuthenticatedImage} from '../../hooks/useAuthenticatedImage';
import {CircularProgress} from '@mui/material';
import {useTheme} from '@mui/material/styles';

/**
 * Component for displaying authenticated images
 * @param {Object} props - Component props
 * @param {string} props.imagePath - The image path from the backend
 * @param {string} props.imageUrl - The direct image URL (for S3)
 * @param {string} props.alt - Alt text for the image
 * @param {Object} props.style - CSS styles for the image
 * @param {string} props.className - CSS class name
 * @param {Function} props.onError - Error handler function
 * @param {Function} props.onLoad - Load handler function
 * @param {boolean} props.showLoading - Whether to show loading spinner
 * @returns {JSX.Element} - The image component
 */
const AuthenticatedImage = ({
                                imagePath,
                                imageUrl,
                                alt = "Image",
                                style = {},
                                className = "",
                                onError,
                                onLoad,
                                showLoading = true,
                                ...props
                            }) => {
    const theme = useTheme();
    // Always call the hook to maintain the Rules of Hooks
    const {imageUrl: authenticatedUrl, loading, error} = useAuthenticatedImage(imagePath, imageUrl);

    // Determine which URL to use - prioritize imageUrl (public URL) over authenticated URL
    const finalImageUrl = imageUrl || authenticatedUrl;
    console.log('AuthenticatedImage render - finalImageUrl:', finalImageUrl, 'loading:', loading, 'error:', error, 'props:', {
        imagePath,
        imageUrl
    });

    const handleError = (e) => {
        console.log('Image error:', e.target.src, e);
        if (onError) {
            onError(e);
        } else {
            // Default error handling - hide the image on error
            e.target.style.display = 'none';
        }
    };

    const handleLoad = (e) => {
        console.log('Image loaded successfully:', e.target.src);
        if (onLoad) {
            onLoad(e);
        }
    };

    // Only show loading if we don't have a direct imageUrl and showLoading is true
    if (loading && showLoading && !imageUrl) {
        console.log('Showing loading spinner');
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    ...style
                }}
                className={className}
            >
                <CircularProgress size={24}/>
            </div>
        );
    }

    if (error && !finalImageUrl) {
        console.log('Showing error state:', error);
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: theme.palette.grey[100],
                    color: '#666',
                    ...style
                }}
                className={className}
            >
                <span>Image not available</span>
            </div>
        );
    }

    // If no image URL is available, show placeholder
    if (!finalImageUrl) {
        console.log('No finalImageUrl available, showing placeholder');
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: theme.palette.grey[100],
                    color: '#666',
                    ...style
                }}
                className={className}
            >
                <span>No image</span>
            </div>
        );
    }

    console.log('Rendering img tag with src:', finalImageUrl);

    // Ensure proper display and visibility
    const imageStyle = {
        ...style,
        display: 'block',
        maxWidth: '100%',
        height: 'auto',
        ...(style.height && {height: style.height}), // Preserve explicit height
        ...(style.width && {width: style.width}), // Preserve explicit width
    };
    
    return (
        <img
            src={finalImageUrl}
            alt={alt}
            style={imageStyle}
            className={className}
            onError={handleError}
            onLoad={handleLoad}
            {...props}
        />
    );
};

AuthenticatedImage.propTypes = {
    imagePath: PropTypes.string,
    imageUrl: PropTypes.string,
    alt: PropTypes.string,
    style: PropTypes.object,
    className: PropTypes.string,
    onError: PropTypes.func,
    onLoad: PropTypes.func,
    showLoading: PropTypes.bool,
};

export default AuthenticatedImage;
